import { computed, onUnmounted, ref, type Ref } from 'vue'

export type ExtensionStatus = 'checking' | 'connected' | 'missing'

export type ExtensionStatusTextKeyMap = Record<ExtensionStatus, string>

export type ExtensionFreshnessOptions = {
  requireFreshCheck?: boolean
  currentCheckStartedAt?: number | null
}

type ExtensionFreshnessOptionsInput = ExtensionFreshnessOptions | Event

export const resolveExtensionStatusText = (
  status: ExtensionStatus,
  t: (key: string) => string,
  textKeyMap: ExtensionStatusTextKeyMap,
) => {
  return t(textKeyMap[status])
}

export const useExtensionStatus = ({
  extensionLastSeenAt,
  syncToExtension,
  detectionTimeoutMs = 1200,
  onChecked,
}: {
  extensionLastSeenAt: Ref<number | null>
  syncToExtension: () => void
  detectionTimeoutMs?: number
  onChecked?: (status: ExtensionStatus) => void
}) => {
  const extensionStatus = ref<ExtensionStatus>('checking')
  let extensionCheckTimer: ReturnType<typeof setTimeout> | null = null
  let activeCheckStartedAt: number | null = null

  const clearExtensionCheckTimer = () => {
    if (!extensionCheckTimer) return
    clearTimeout(extensionCheckTimer)
    extensionCheckTimer = null
  }

  const resolveCheckStartedAt = (options?: ExtensionFreshnessOptions) => {
    return options?.currentCheckStartedAt ?? activeCheckStartedAt
  }

  const normalizeFreshnessOptions = (
    options?: ExtensionFreshnessOptionsInput,
  ): ExtensionFreshnessOptions | undefined => {
    if (!options) return undefined
    if ('currentCheckStartedAt' in options || 'requireFreshCheck' in options) {
      return options
    }
    return undefined
  }

  const hasConnectedSeenAt = (seenAt: number | null, options?: ExtensionFreshnessOptions) => {
    if (!seenAt) return false

    const checkStartedAt = resolveCheckStartedAt(options)
    const requireFreshCheck = options?.requireFreshCheck ?? Boolean(checkStartedAt)

    if (!requireFreshCheck || !checkStartedAt) return true
    return seenAt >= checkStartedAt
  }

  const finishActiveCheck = (checkStartedAt: number | null) => {
    if (activeCheckStartedAt === checkStartedAt) {
      activeCheckStartedAt = null
    }
  }

  const evaluateExtensionStatus = (options?: ExtensionFreshnessOptions) => {
    extensionStatus.value = hasConnectedSeenAt(extensionLastSeenAt.value, options)
      ? 'connected'
      : 'missing'
  }

  const checkExtensionStatus = (input?: ExtensionFreshnessOptionsInput) => {
    const options = normalizeFreshnessOptions(input)
    clearExtensionCheckTimer()
    const checkStartedAt = options?.currentCheckStartedAt ?? Date.now()
    const requireFreshCheck = options?.requireFreshCheck ?? true
    activeCheckStartedAt = checkStartedAt
    extensionStatus.value = 'checking'
    syncToExtension()
    extensionCheckTimer = setTimeout(() => {
      evaluateExtensionStatus({ requireFreshCheck, currentCheckStartedAt: checkStartedAt })
      onChecked?.(extensionStatus.value)
      extensionCheckTimer = null
      finishActiveCheck(checkStartedAt)
    }, detectionTimeoutMs)
  }

  const handleExtensionSeenAtChange = (
    seenAt: number | null,
    enabled = true,
    options?: ExtensionFreshnessOptions,
  ) => {
    if (!enabled) return
    if (hasConnectedSeenAt(seenAt, options)) {
      const completedActiveCheck =
        activeCheckStartedAt !== null && extensionStatus.value === 'checking'
      clearExtensionCheckTimer()
      finishActiveCheck(resolveCheckStartedAt(options))
      extensionStatus.value = 'connected'
      if (completedActiveCheck) onChecked?.('connected')
      return
    }
    if (extensionStatus.value !== 'checking') {
      extensionStatus.value = 'missing'
    }
  }

  const isExtensionConnected = computed(() => extensionStatus.value === 'connected')

  onUnmounted(() => {
    clearExtensionCheckTimer()
  })

  return {
    extensionStatus,
    isExtensionConnected,
    clearExtensionCheckTimer,
    checkExtensionStatus,
    handleExtensionSeenAtChange,
  }
}
